// LOCATION: admin-portal/src/pages/AccessManagement.jsx

import React, { useEffect, useState } from 'react';
import { db } from '../firebase'; 
import { collection, getDocs, doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import './AccessManagement.css'; 

const AccessManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [staffList, setStaffList] = useState([]); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingAdminEmail, setEditingAdminEmail] = useState(null); // Track if we are editing

  // Form State
  const [selectedStaffUid, setSelectedStaffUid] = useState('');
  const [permissions, setPermissions] = useState({
    super_admin: false,
    admin_access: false,
    user_management: false,
    subscribers: false,
    events: false,
    staff_management: false,
    journey_logs: false // ✅ ADDED: Journey Logs permission
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'admin_access'), (snapshot) => {
      const fetchedAdmins = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAdmins(fetchedAdmins);
      setLoading(false);
    });

    const fetchStaff = async () => {
      try {
        const snap = await getDocs(collection(db, 'staff'));
        const staff = snap.docs.map(doc => ({ 
          id: doc.id, 
          name: doc.data().name || 'Unknown Staff', 
          email: doc.data().email 
        }));
        setStaffList(staff);
      } catch (err) {
        console.error("Error loading staff:", err);
      }
    };

    fetchStaff();
    return () => unsubscribe();
  }, []);

  const handlePermissionChange = (key) => {
    if (key === 'super_admin') {
      const newValue = !permissions.super_admin;
      setPermissions({
        super_admin: newValue,
        admin_access: newValue,
        user_management: newValue,
        subscribers: newValue,
        events: newValue,
        staff_management: newValue,
        journey_logs: newValue // ✅ Toggle journey_logs with super_admin
      });
    } else {
      setPermissions(prev => {
        const updated = { ...prev, [key]: !prev[key] };
        if (updated[key] === false) updated.super_admin = false;
        return updated;
      });
    }
  };

  const handleEdit = (admin) => {
    setEditingAdminEmail(admin.email);
    setSelectedStaffUid(admin.staffUid || '');
    setPermissions({
      super_admin: admin.permissions.super_admin || false,
      admin_access: admin.permissions.admin_access || false,
      user_management: admin.permissions.user_management || false,
      subscribers: admin.permissions.subscribers || false,
      events: admin.permissions.events || false,
      staff_management: admin.permissions.staff_management || false,
      journey_logs: admin.permissions.journey_logs || false
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const emailToUse = editingAdminEmail || staffList.find(s => s.id === selectedStaffUid)?.email;
    const nameToUse = editingAdminEmail ? admins.find(a => a.email === editingAdminEmail)?.name : staffList.find(s => s.id === selectedStaffUid)?.name;

    if (!emailToUse) return alert("Please select a staff member.");

    try {
      await setDoc(doc(db, 'admin_access', emailToUse), {
        name: nameToUse,
        email: emailToUse,
        staffUid: selectedStaffUid,
        permissions: permissions,
        updatedAt: new Date()
      }, { merge: true });
      
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving access:", error);
      alert("Failed to save access.");
    }
  };

  const handleDelete = async (email) => {
    if (window.confirm("Revoke access for this user?")) {
      await deleteDoc(doc(db, 'admin_access', email));
    }
  };

  const resetForm = () => {
    setSelectedStaffUid('');
    setEditingAdminEmail(null);
    setPermissions({
      super_admin: false,
      admin_access: false,
      user_management: false,
      subscribers: false,
      events: false,
      staff_management: false,
      journey_logs: false
    });
  };

  if (loading) return <div className="container">Loading Access List...</div>;

  return (
    <div className="container">
      <div className="header">
        <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
            <span style={{fontSize:'28px'}}>🔐</span>
            <h1 style={{margin:0}}>Access Management</h1>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{width:'20%'}}>Staff Name</th>
              <th style={{width:'25%'}}>Email</th>
              <th style={{width:'35%'}}>Permissions</th>
              <th style={{width:'20%'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.id}>
                <td style={{fontWeight:'600', color:'#111827'}}>{admin.name}</td>
                <td style={{color:'#6b7280'}}>{admin.email}</td>
                <td>
                  <div className="perm-tags">
                    {admin.permissions.super_admin ? (
                      <span className="badge badge-purple">ALL ACCESS</span>
                    ) : (
                      <>
                        {admin.permissions.admin_access && <span className="badge badge-blue">Admin</span>}
                        {admin.permissions.user_management && <span className="badge badge-green">Users</span>}
                        {admin.permissions.staff_management && <span className="badge badge-blue">Staff</span>}
                        {admin.permissions.subscribers && <span className="badge badge-orange">Subs</span>}
                        {admin.permissions.events && <span className="badge badge-red">Events</span>}
                        {admin.permissions.journey_logs && <span className="badge badge-purple">Logs</span>}
                      </>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{display:'flex', gap:'8px'}}>
                    <button onClick={() => handleEdit(admin)} className="action-btn btn-primary" style={{padding:'6px 12px', fontSize:'12px'}}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(admin.email)} className="action-btn btn-danger" style={{padding:'6px 12px', fontSize:'12px'}}>
                      Revoke
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="fab" onClick={() => { resetForm(); setIsModalOpen(true); }} title="Add Admin">+</button>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingAdminEmail ? 'Update Access' : 'Grant New Access'}</h2>
            
            <label className="modal-label">Select Staff Member</label>
            <select 
              value={selectedStaffUid} 
              onChange={(e) => setSelectedStaffUid(e.target.value)}
              className="modal-select"
              disabled={!!editingAdminEmail} // Prevent changing email during edit
            >
              <option value="">-- Search Staff --</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>

            <label className="modal-label">Assign Permissions</label>
            <div className="checkbox-group">
              <label className="checkbox-row highlight-row">
                <input 
                  type="checkbox" 
                  checked={permissions.super_admin} 
                  onChange={() => handlePermissionChange('super_admin')} 
                />
                <span style={{flex:1}}>Super Admin (Full Access)</span>
              </label>

              <div style={{opacity: permissions.super_admin ? 0.5 : 1, pointerEvents: permissions.super_admin ? 'none' : 'auto'}}>
                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.admin_access} onChange={() => handlePermissionChange('admin_access')} />
                    <span>Access Management Page</span>
                </label>
                
                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.staff_management} onChange={() => handlePermissionChange('staff_management')} />
                    <span>Staff Management</span>
                </label>

                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.user_management} onChange={() => handlePermissionChange('user_management')} />
                    <span>User Management</span>
                </label>
                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.subscribers} onChange={() => handlePermissionChange('subscribers')} />
                    <span>Subscribers</span>
                </label>
                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.events} onChange={() => handlePermissionChange('events')} />
                    <span>Event Analytics</span>
                </label>
                {/* ✅ ADDED: Journey Logs Checkbox */}
                <label className="checkbox-row">
                    <input type="checkbox" checked={permissions.journey_logs} onChange={() => handlePermissionChange('journey_logs')} />
                    <span>Journey Logs & Billing</span>
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="cancel-btn">Cancel</button>
              <button onClick={handleSave} className="save-btn">
                {editingAdminEmail ? 'Update Access' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessManagement;